/**
 * Participant search input validation.
 * Implements the minimum character rule with exceptions for phone, email, and dates.
 *
 * Rules:
 * - Free text: minimum 3 characters
 * - Phone numbers: digits/punctuation () -+ space with ≥ 3 digits: allowed
 * - Email: contains @ : allowed
 * - Dates: M/D/YYYY or M-D-YYYY pattern: allowed
 * - Shorter input: should not fire search
 * - Empty: allowed (unfiltered list)
 */

export interface SearchValidationResult {
	isValid: boolean;
	helperText?: string;
}

/**
 * Validates if a search input meets minimum character requirements.
 * Returns validation result with optional helper text for inline display.
 */
export function validateSearchInput(input: string | null | undefined): SearchValidationResult {
	if (!input) {
		// Empty input is valid (shows unfiltered list)
		return { isValid: true };
	}

	const trimmed = input.trim();

	if (trimmed.length === 0) {
		// Whitespace-only is treated as empty
		return { isValid: true };
	}

	// Check if input looks like a date (M/D/YYYY or M-D-YYYY)
	if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(trimmed)) {
		return { isValid: true };
	}

	// Check if input contains @ (likely email)
	if (trimmed.includes("@")) {
		return { isValid: true };
	}

	// Check if input is only phone punctuation and digits, with at least 3 digits
	// Phone charset: digits, (), -, +, space
	const phoneOnly = /^[0-9()\-+ ]+$/.test(trimmed);
	if (phoneOnly) {
		const digitCount = (trimmed.match(/\d/g) || []).length;
		if (digitCount >= 3) {
			return { isValid: true };
		}
		// Phone-like input but not enough digits
		return {
			isValid: false,
			helperText: "Minimum 3 digits required for phone search",
		};
	}

	// For any other input, require minimum 3 characters
	if (trimmed.length >= 3) {
		return { isValid: true };
	}

	// Input is too short and doesn't match any exception
	return {
		isValid: false,
		helperText: "Minimum 3 characters required",
	};
}

/**
 * Checks if a search should actually fire based on input validation.
 * Returns true if the search should proceed.
 */
export function shouldFireSearch(input: string | null | undefined): boolean {
	return validateSearchInput(input).isValid;
}
